'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { SK } from '@/constants';

/**
 * Wraps the auth pages (sign in, sign up, OTP sign in, forgot password) so an already
 * signed-in visitor is sent to /portal instead of being shown a login form they do not need.
 * Once they sign out, localStorage is cleared and these pages become reachable again — that
 * falls out of the storage check rather than needing any extra logout wiring.
 *
 * Why localStorage is read directly instead of useUser():
 * UserProvider rehydrates `user` inside an effect, so `isLoggedIn` is false on the first
 * render even for a signed-in visitor. Gating on it would render the full login form, then
 * yank it away a frame later. Reading storage in this component's own first effect decides
 * before anything is painted.
 *
 * `null` is rendered until the check completes. That costs a frame on a page whose content
 * is client-rendered anyway, and it is the only way to guarantee the form never flashes.
 */
const isSignedIn = (): boolean => {
  try {
    // The user object is the source of truth (userContext writes it on login, clears it on
    // logout). authToken is checked too because Navbar's logout removes them separately, and
    // a half-cleared state should count as signed out.
    const stored = window.localStorage.getItem(SK.NEXTUSERINFODATA);
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return Boolean(parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0);
  } catch {
    // Corrupt JSON, or storage blocked (private mode / third-party cookie rules).
    // Treat as signed out so the login form stays reachable rather than trapping the visitor.
    return false;
  }
};

const RedirectIfAuthenticated = ({
  children,
  to = '/portal',
}: {
  children: React.ReactNode;
  to?: string;
}) => {
  const router = useRouter();
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (isSignedIn()) {
      // replace, not push: the visitor should not be able to press Back into the login form
      // they were just redirected away from.
      router.replace(to);
      return;
    }
    setAllowed(true);
  }, [router, to]);

  if (!allowed) return null;
  return <>{children}</>;
};

export default RedirectIfAuthenticated;
